import { getStorage, ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { app } from "../firebase-config.js";

/**
 * MediaService
 * Handles image compression and file uploads to Firebase Storage.
 * Centralizes logic previously duplicated in editors.
 */
export const MediaService = {
    /**
     * Compresses an image file to WebP format.
     * @param {File} file - Original image file.
     * @param {number} quality - Compression quality (0.0 to 1.0).
     * @returns {Promise<Blob>} - Compressed WebP blob.
     */
    compressImage: async (file, quality = 0.8) => {
        return new Promise((resolve, reject) => {
            const objectUrl = URL.createObjectURL(file);
            const img = new Image();

            img.onload = () => {
                URL.revokeObjectURL(objectUrl);
                const canvas = document.createElement('canvas');
                // Strict No-Resize: Maintain original dimensions as per requirements
                canvas.width = img.width;
                canvas.height = img.height;

                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                canvas.toBlob((blob) => {
                    if (!blob) {
                        reject(new Error("Canvas compression failed"));
                        return;
                    }
                    console.log(`[MediaService] Compressed: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB) -> ${(blob.size / 1024 / 1024).toFixed(2)}MB`);
                    resolve(blob);
                }, 'image/webp', quality);
            };

            img.onerror = (e) => {
                URL.revokeObjectURL(objectUrl);
                reject(new Error("Image load failed"));
            };

            img.src = objectUrl;
        });
    },

    /**
     * Uploads a blob to Firebase Storage.
     * @param {Blob} blob - File data to upload.
     * @param {string} path - Storage path (e.g., 'series/123/cover.webp').
     * @param {Function} [onProgress] - Optional callback (0-100).
     * @returns {Promise<string>} - Download URL.
     */
    uploadImage: async (blob, path, onProgress) => {
        // Auth check should be done by caller or guarded here if critical.
        // Assuming caller context validity for now, but importing auth strictly if needed.
        const { getAuth } = await import("firebase/auth");
        const auth = getAuth();
        if (!auth.currentUser) throw new Error("User not logged in");

        const storage = getStorage(app);
        const storageRef = ref(storage, path);
        const metadata = { contentType: 'image/webp' };

        return new Promise((resolve, reject) => {
            const uploadTask = uploadBytesResumable(storageRef, blob, metadata);

            uploadTask.on('state_changed',
                (snapshot) => {
                    if (onProgress) {
                        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                        onProgress(progress);
                    }
                },
                (error) => {
                    if (error.code === 'storage/canceled') return;
                    console.error("[MediaService] Upload Error:", error);
                    reject(new Error(`Upload failed: ${error.message}`));
                },
                async () => {
                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                    resolve(downloadURL);
                }
            );
        });
    },

    /**
     * Converts a Suno share link to direct MP3 URL.
     * @param {string} url - Original URL.
     * @returns {string} - Direct CDN URL or original if not applicable.
     */
    convertToSunoMp3: (url) => {
        if (!url) return '';
        url = url.trim();
        if (url.endsWith('.mp3') || url.endsWith('.wav')) return url;

        // Supports: suno.com/song/{uuid}, /song/{uuid}?params...
        const sunoRegex = /\/song\/([0-9a-fA-F-]{36})/;
        const match = url.match(sunoRegex);

        if (match && match[1]) {
            return `https://cdn1.suno.ai/${match[1]}.mp3`;
        }
        return url;
    }
};
